import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { lazy, useEffect } from 'react';
import { createBrowserRouter, Navigate, Outlet, RouterProvider, useNavigate } from 'react-router';

import { Layout, MENU_PATH } from './components/Layout';
import { ErrorState, Loading } from './components/ui';
import { ApiError, setUnauthenticatedHandler } from './lib/api';
import { SessionProvider, useSession, useSessionQuery } from './lib/session';
import { LoginPage } from './pages/Login';

const page = <K extends string>(loader: () => Promise<Record<K, React.ComponentType>>, name: K) =>
  lazy(() => loader().then((m) => ({ default: m[name] })));

const DashboardPage = page(() => import('./pages/Dashboard'), 'DashboardPage');
const MembersPage = page(() => import('./pages/Members'), 'MembersPage');
const MemberDetailPage = page(() => import('./pages/Members'), 'MemberDetailPage');
const ConfigPage = page(() => import('./pages/Config'), 'ConfigPage');
const PlatformsPage = page(() => import('./pages/Platforms'), 'PlatformsPage');
const ShoppingOrdersPage = page(() => import('./pages/ShoppingOrders'), 'ShoppingOrdersPage');
const ShoppingOrderDetailPage = page(() => import('./pages/ShoppingOrders'), 'ShoppingOrderDetailPage');
const PackageOrdersPage = page(() => import('./pages/PackageOrders'), 'PackageOrdersPage');
const PackageOrderDetailPage = page(() => import('./pages/PackageOrders'), 'PackageOrderDetailPage');
const ManualTransferPage = page(() => import('./pages/ManualTransfer'), 'ManualTransferPage');
const WalletPage = page(() => import('./pages/Wallet'), 'WalletPage');
const WithdrawalsPage = page(() => import('./pages/Withdrawals'), 'WithdrawalsPage');
const WithdrawalDetailPage = page(() => import('./pages/Withdrawals'), 'WithdrawalDetailPage');
const FinancePage = page(() => import('./pages/Finance'), 'FinancePage');
const TaxPage = page(() => import('./pages/Tax'), 'TaxPage');
const TaxRecordDetailPage = page(() => import('./pages/Tax'), 'TaxRecordDetailPage');
const ReportsPage = page(() => import('./pages/Reports'), 'ReportsPage');
const AuditLogsPage = page(() => import('./pages/AuditLogs'), 'AuditLogsPage');
const AdminRolesPage = page(() => import('./pages/AdminRoles'), 'AdminRolesPage');

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 15_000,
      retry: (count, error) =>
        !(error instanceof ApiError && error.status >= 400 && error.status < 500) && count < 2,
    },
  },
});

function RequireAdmin() {
  const navigate = useNavigate();
  const session = useSessionQuery();
  useEffect(() => {
    setUnauthenticatedHandler(() => {
      queryClient.clear();
      void navigate('/masuk', { replace: true });
    });
  }, [navigate]);
  if (session.isPending) return <Loading label="Menyiapkan sesi…" />;
  if (session.error)
    return (
      <div className="p-6">
        <ErrorState error={session.error} onRetry={() => void session.refetch()} />
      </div>
    );
  if (!session.data) return <Navigate to="/masuk" replace />;
  return (
    <SessionProvider value={session.data}>
      <Outlet />
    </SessionProvider>
  );
}

/** Beranda = menu pertama yang diizinkan. */
function Home() {
  const { menus } = useSession();
  const first = menus[0];
  return first ? (
    <Navigate to={MENU_PATH[first.menu]} replace />
  ) : (
    <p className="p-6 text-slate-700">Role ini belum punya akses menu apa pun.</p>
  );
}

const router = createBrowserRouter([
  { path: '/masuk', element: <LoginPage /> },
  {
    element: <RequireAdmin />,
    children: [
      {
        element: <Layout />,
        children: [
          { path: '/', element: <Home /> },
          { path: '/dashboard', element: <DashboardPage /> },
          { path: '/member', element: <MembersPage /> },
          { path: '/member/:accountId', element: <MemberDetailPage /> },
          { path: '/konfigurasi/:category', element: <ConfigPage /> },
          { path: '/platform', element: <PlatformsPage /> },
          { path: '/transaksi-belanja', element: <ShoppingOrdersPage /> },
          { path: '/transaksi-belanja/:id', element: <ShoppingOrderDetailPage /> },
          { path: '/transaksi-paket', element: <PackageOrdersPage /> },
          { path: '/transaksi-paket/transfer-manual', element: <ManualTransferPage /> },
          { path: '/transaksi-paket/:id', element: <PackageOrderDetailPage /> },
          { path: '/wallet', element: <WalletPage /> },
          { path: '/pencairan', element: <WithdrawalsPage /> },
          { path: '/pencairan/:id', element: <WithdrawalDetailPage /> },
          { path: '/keuangan', element: <FinancePage /> },
          { path: '/pajak', element: <TaxPage /> },
          { path: '/pajak/:id', element: <TaxRecordDetailPage /> },
          { path: '/laporan', element: <ReportsPage /> },
          { path: '/audit-log', element: <AuditLogsPage /> },
          { path: '/admin-role', element: <AdminRolesPage /> },
          { path: '*', element: <Navigate to="/" replace /> },
        ],
      },
    ],
  },
]);

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <RouterProvider router={router} />
    </QueryClientProvider>
  );
}
